import {
    Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import React, { useState } from 'react';
import {
    CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer,
    ScatterChart, Scatter, ZAxis, ReferenceLine, Cell,
    PieChart, Pie, Legend,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { FadedPaper } from '../../theme/StyledFields';
import KeyTakeaway from './KeyTakeaway';
import VizToggle, { VizType } from './VizToggle';
import { CorrelationSection as CorrelationSectionData } from './types';

const RISK_COLORS: Record<string, string> = {
    'Both Low': '#d32f2f',
    'Neither Low': '#2e7d32',
};

function getRiskColor(category: string, fallbackA: string, fallbackB: string): string {
    if (RISK_COLORS[category]) return RISK_COLORS[category];
    if (category.includes('Only')) return '#ed6c02';
    return '#999';
}

interface Props {
    data: CorrelationSectionData;
}

const CorrelationSection: React.FC<Props> = ({ data }) => {
    const [viz, setViz] = useState<VizType>('table');
    const theme = useTheme();

    const scatterData = data.rows.map((r) => ({
        x: r.score_a,
        y: r.score_b,
        name: r.name,
        risk: r.risk_category,
    }));

    const pieData = Object.entries(data.risk_summary).map(([name, value]) => ({
        name,
        value,
    }));

    const PIE_COLORS = pieData.map(({ name }) =>
        getRiskColor(name, theme.palette.warning.main, theme.palette.info.main)
    );

    return (
        <FadedPaper sx={{ p: 3, mb: 3 }} data-testid="section-correlation">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h5">
                    5. Overlapping Challenges — {data.domain_a} vs. {data.domain_b}
                </Typography>
                <VizToggle value={viz} options={['scatter', 'pie', 'table']} onChange={setViz} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Since these are the team's two weakest domains, this checks whether individuals
                who score low in one also score low in the other (threshold: &lt; 3.0).
            </Typography>

            {viz === 'scatter' && (
                <ResponsiveContainer width="100%" height={400}>
                    <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <CartesianGrid />
                        <XAxis
                            type="number" dataKey="x" name={data.domain_a}
                            domain={[0, 5]} label={{ value: data.domain_a, position: 'bottom', offset: 0 }}
                        />
                        <YAxis
                            type="number" dataKey="y" name={data.domain_b}
                            domain={[0, 5]} label={{ value: data.domain_b, angle: -90, position: 'insideLeft' }}
                        />
                        <ZAxis range={[60, 60]} />
                        <Tooltip
                            formatter={(val: number) => val.toFixed(2)}
                            labelFormatter={(_: any, payload: readonly any[]) =>
                                payload?.[0]?.payload?.name || ''
                            }
                        />
                        <ReferenceLine x={3} stroke={theme.palette.error.main} strokeDasharray="3 3" />
                        <ReferenceLine y={3} stroke={theme.palette.error.main} strokeDasharray="3 3" />
                        <Scatter data={scatterData} fill={theme.palette.primary.main}>
                            {scatterData.map((entry, idx) => (
                                <Cell
                                    key={idx}
                                    fill={getRiskColor(entry.risk, theme.palette.warning.main, theme.palette.info.main)}
                                />
                            ))}
                        </Scatter>
                    </ScatterChart>
                </ResponsiveContainer>
            )}

            {viz === 'pie' && (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <ResponsiveContainer width={350} height={300}>
                        <PieChart>
                            <Pie
                                data={pieData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                label={({ name, value }) => `${name}: ${value}`}
                            >
                                {pieData.map((_, idx) => (
                                    <Cell key={idx} fill={PIE_COLORS[idx]} />
                                ))}
                            </Pie>
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </Box>
            )}

            {viz === 'table' && (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell align="right">{data.domain_a}</TableCell>
                                <TableCell align="right">{data.domain_b}</TableCell>
                                <TableCell align="right">Diff</TableCell>
                                <TableCell>Risk Category</TableCell>
                                <TableCell align="right">Total</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.rows.map((r) => (
                                <TableRow key={r.user_id}>
                                    <TableCell>{r.name}</TableCell>
                                    <TableCell align="right">{r.score_a}</TableCell>
                                    <TableCell align="right">{r.score_b}</TableCell>
                                    <TableCell align="right">{r.diff > 0 ? '+' : ''}{r.diff}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={r.risk_category}
                                            size="small"
                                            sx={{
                                                backgroundColor: getRiskColor(r.risk_category, theme.palette.warning.main, theme.palette.info.main),
                                                color: '#fff',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{r.total_score}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <KeyTakeaway text={data.key_takeaway} />
        </FadedPaper>
    );
};

export default CorrelationSection;
